import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AssistantRuntimeProvider, useAui, useLocalRuntime } from '@assistant-ui/react';

import {
  deleteConversation as deleteConversationAPI,
  filePart,
  interruptPart,
  createConversation as createConversationAPI,
  forkConversation as forkConversationAPI,
  listConversations,
  listItems,
  pinConversation as pinConversationAPI,
  renameConversation as renameConversationAPI,
  toRepository,
} from '../../services/conversations';
import { streamResponse } from '../../services/responses';
import { createAttachmentAdapter } from './attachments-adapter';
import { useSelection } from './SelectionContext';

// The backend's latest progress line. Not a message part: it is transient, it
// belongs to the run rather than to the answer, and it disappears when the run
// ends.
const ProgressContext = createContext('');

export const useRunProgress = () => useContext(ProgressContext);

// Chat history: the list the sidebar renders, plus opening and deleting one.
const ChatsContext = createContext({
  chats: [],
  current: null,
  isLoading: false,
  newChat: () => {},
  openChat: () => {},
  renameChat: () => {},
  removeChat: () => {},
  forkChat: () => {},
  pinChat: () => {},
  answerQuestion: () => {},
  reload: () => {},
});

export const useChats = () => useContext(ChatsContext);

// Kept for the callers that only start a new chat.
export const useNewChat = () => useChats().newChat;

// Trimmed, because a composer is a textarea and Shift+Enter leaves whatever
// somebody was lining up in it. Those newlines reach the model as content, ride
// in the stored message, and then ride in the prompt again on every later turn.
//
// Both ends, not just the trailing one: assistant-ui decides a composer is
// empty with text.trim(), so anything narrower would leave our idea of empty
// and theirs disagreeing, and a leading blank line is not wanted either.
//
// Nothing is lost by doing it here. A message that is ONLY whitespace never
// arrives: the library's canSend is false for it and send() returns early
// unless there is an attachment, and a file sent with no words is exactly the
// case inputFrom already handles by omitting the text part.
const textOf = (message) =>
  (message?.content || [])
    .filter((part) => part.type === 'text')
    .map((part) => part.text)
    .join('')
    .trim();

// Attachments travel as file ids, never bytes or URLs: the file is already
// stored and the server resolves the id against this conversation.
//
// Read off message.attachments, from the fileId our adapter's add() and the
// history loader put there; the attachment's own id is assistant-ui's handle,
// and for an upload it's ours, not the server's. The content parts carry a
// fileId too, but that is our own field on assistant-ui's part shape and there
// is no promise it survives being merged into the message.
const fileIdsOf = (message) => {
  const fromAttachments = (message?.attachments || []).map((a) => a.fileId).filter(Boolean);
  if (fromAttachments.length > 0) {
    return fromAttachments;
  }

  return (message?.content || [])
    .filter((part) => part.type === 'image' || part.type === 'file')
    .map((part) => part.fileId)
    .filter(Boolean);
};

// A message with no attachment stays a plain string, which is the shape the
// endpoint has always taken. Only a turn carrying files pays for the longer
// form, so nothing else changes shape underneath us.
function inputFrom(message) {
  const text = textOf(message);
  const fileIds = fileIdsOf(message);

  if (fileIds.length === 0) {
    return text;
  }

  const content = fileIds.map((fileId) => ({ type: 'input_file', file_id: fileId }));
  if (text) {
    content.unshift({ type: 'input_text', text });
  }

  // "type" is not decoration: without it the server's union can't tell which
  // kind of item this is and drops the whole list, leaving a request with no
  // input at all.
  return [{ type: 'message', role: 'user', content }];
}

// Builds the assistant message as an ordered parts array. Reasoning, tool calls
// and text are separate parts, in the order the backend produced them, which is
// what assistant-ui renders from.
function partBuilder() {
  const parts = [];
  const tools = new Map();
  const files = new Set();

  // Appends to the tail part only when it is the same item: two reasoning
  // blocks in a row are two items, and merging them would collapse them into
  // one bubble.
  const append = (type, { delta, itemId }) => {
    const last = parts[parts.length - 1];

    if (last?.type === type && last.itemId === itemId) {
      last.text += delta;
      return;
    }

    parts.push({ type, text: delta, itemId });
  };

  return {
    text(chunk) {
      append('text', chunk);
    },

    reasoning(chunk) {
      append('reasoning', chunk);
    },

    interrupt(event) {
      parts.push(interruptPart(event));
    },

    // A file the run produced. Keyed by id so a repeat (a retry, a resync)
    // replaces rather than stacks.
    attachment(file) {
      if (!file?.id || files.has(file.id)) {
        return;
      }

      files.add(file.id);
      parts.push(filePart(file));
    },

    tool(chunk) {
      let part = tools.get(chunk.callId);

      if (!part) {
        part = {
          type: 'tool-call',
          toolCallId: chunk.callId,
          toolName: chunk.name,
          args: {},
          argsText: chunk.args || '',
        };
        tools.set(chunk.callId, part);
        parts.push(part);
      }

      if (chunk.args) {
        part.argsText = chunk.args;
        try {
          part.args = JSON.parse(chunk.args);
        } catch {
          // a partial parse is fine, the renderer shows argsText
        }
      }

      if (chunk.state === 'completed') {
        // a tool with nothing to return still finished: leaving result
        // undefined would keep the card saying "running"
        part.result = chunk.result ?? '';
      }

      if (chunk.state === 'failed') {
        part.result = chunk.error?.message || 'the tool failed';
        part.isError = true;
      }
    },

    // a fresh array each time, or React sees the same reference and skips.
    // itemId is our bookkeeping, not part of assistant-ui's part shape.
    snapshot: () => parts.map(({ itemId, ...part }) => ({ ...part })),
  };
}

export default function ChatRuntimeProvider({ children }) {
  const { project, bot, limits, canSendImages } = useSelection();
  const [progress, setProgress] = useState('');

  // set by the chats provider below, called by the adapter when a run finishes
  const refresh = useRef(() => {});
  const refreshChats = useCallback(() => refresh.current(), []);

  const resync = useRef(() => {});
  const resyncThread = useCallback(() => resync.current(), []);

  // The answer to a paused run's question, waiting for the run that carries it.
  // Kept here rather than written into the thread because the server is the
  // record: it stores the decision on the question, and the resync after the
  // run brings it back as part of the transcript.
  const pending = useRef(null);

  // The backend creates a conversation on the first turn and names it in the
  // response; every later turn in this thread references it.
  const conversation = useRef(null);

  const adapter = useMemo(
    () => ({
      async *run({ messages, abortSignal }) {
        const last = messages[messages.length - 1];
        const startedWith = `${project}/${bot}`;

        // A run that stopped to ask something resumes by answering it, not by
        // sending a message. Taken rather than read, so a decision is used by
        // exactly one run: the next ordinary message must not re-answer a
        // question that was already settled.
        const answer = pending.current;
        pending.current = null;

        const stream = streamResponse({
          model: startedWith,
          input: answer ? undefined : inputFrom(last),
          approval: answer,
          conversation: conversation.current,
          signal: abortSignal,
        });

        const builder = partBuilder();
        setProgress('');

        try {
          // driven by hand rather than for-await: the generator's RETURN value
          // carries the final response, and for-await throws it away
          for (;;) {
            const { value, done } = await stream.next();

            if (done) {
              // a run that outlived a bot switch must not bind its old
              // conversation to the new selection
              if (value?.conversation?.id && startedWith === `${project}/${bot}`) {
                conversation.current = value.conversation.id;
              }
              break;
            }

            if (value.kind === 'progress') {
              setProgress(value.message);
              continue;
            }

            if (value.kind === 'interrupt') {
              builder.interrupt(value.interrupt);
              yield { content: builder.snapshot() };
              continue;
            }

            if (value.kind === 'attachment') {
              builder.attachment(value.file);
              yield { content: builder.snapshot() };
              continue;
            }

            if (value.kind === 'text') builder.text(value);
            if (value.kind === 'reasoning') builder.reasoning(value);
            if (value.kind === 'tool') {
              builder.tool(value);
              setProgress(value.message || '');
            }

            // each yield carries the whole message so far, not just the delta
            yield { content: builder.snapshot() };
          }
        } finally {
          setProgress('');
          // on abort the consumer closes us, which must close the response body
          await stream.return?.();

          // the turn is persisted by now, so a chat created by this run (or its
          // moved activity) shows up in the sidebar
          refreshChats();
          resyncThread();
        }
      },
    }),
    [project, bot, refreshChats, resyncThread, pending],
  );

  // A file has to belong to a chat before it can be uploaded, and the user may
  // attach before they type. Normally the first turn creates the chat; when an
  // attachment gets there first, this does, and the run then finds it already
  // there like any later turn would.
  const ensureConversation = useCallback(async () => {
    if (conversation.current) return conversation.current;

    const created = await createConversationAPI(project, bot);
    conversation.current = created.id;
    refreshChats();

    return created.id;
  }, [project, bot, refreshChats]);

  const attachments = useMemo(
    () => createAttachmentAdapter({ limits, canSendImages, ensureConversation }),
    [limits, canSendImages, ensureConversation],
  );

  const runtime = useLocalRuntime(adapter, { adapters: { attachments } });

  return (
    <ProgressContext.Provider value={progress}>
      <AssistantRuntimeProvider runtime={runtime}>
        <NewChatOnSwitch
          conversation={conversation}
          refresh={refresh}
          resync={resync}
          pending={pending}
        >
          {children}
        </NewChatOnSwitch>
      </AssistantRuntimeProvider>
    </ProgressContext.Provider>
  );
}

// Owns "start a fresh chat". Lives inside the runtime provider because that is
// where the thread is reachable.
function NewChatOnSwitch({ conversation, refresh, resync, pending, children }) {
  const { project, bot, setProject, setBot } = useSelection();
  const aui = useAui();

  const [chats, setChats] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [current, setCurrent] = useState(null);

  // bumped on every open, including re-opening the current chat: the scroll
  // compensation can't hang off `current` alone, which doesn't change then
  const [opened, setOpened] = useState(0);

  // guards against out-of-order chat opens
  const opening = useRef(0);

  // A chat belongs to one project/bot pair, so a different bot is a different
  // chat rather than a continuation of this one.
  const selection = `${project}/${bot}`;
  const previous = useRef(selection);

  const reload = useCallback(async () => {
    setIsLoading(true);

    try {
      setChats(await listConversations());
      // the adapter writes conversation.current when a run creates a chat, and
      // it has no way to reach this state: reload is the one place both are set
      setCurrent(conversation.current);
    } catch (err) {
      console.error('cannot load chats:', err);
      setChats([]);
    } finally {
      setIsLoading(false);
    }
  }, [conversation]);

  useEffect(() => {
    reload();
    refresh.current = reload;
  }, [reload, refresh]);

  // Re-imports the stored thread over the live one after a run. The point is
  // the ids: a streamed message carries assistant-ui's local id, while anything
  // acting on one message server-side (branching, for one) needs ours. By then
  // both hold the same turns, so the swap is invisible and every message ends
  // up with the id the backend knows it by.
  const resyncThread = useCallback(async () => {
    const id = conversation.current;
    if (!id) return;

    const seq = opening.current;

    try {
      const stored = await listItems(id);

      // a chat switch during the run wins: importing here would land the old
      // chat's messages in the thread the user just opened
      if (seq !== opening.current || conversation.current !== id) return;

      aui.thread.import(toRepository(stored));
    } catch (err) {
      console.error('cannot resync chat:', err);
    }
  }, [aui, conversation]);

  useEffect(() => {
    resync.current = resyncThread;
  }, [resync, resyncThread]);

  const stopRunning = useCallback(() => {
    // a run still streaming belongs to the chat we are leaving
    if (aui.thread.getState().isRunning) {
      aui.thread.cancelRun();
    }
  }, [aui]);

  // switchToNewThread resolves once the runtime has actually switched. Not
  // awaiting it is what made opening a chat show a blank thread: the import
  // landed in the thread being left behind.
  const newChat = useCallback(async () => {
    // leaving for a fresh chat abandons any open still in flight, or it lands
    // on top of the empty thread a moment later
    opening.current += 1;

    stopRunning();

    conversation.current = null;
    setCurrent(null);

    // callers fire this from click handlers and effects without awaiting, so a
    // rejection here would surface as an unhandled one
    try {
      await aui.threads.switchToNewThread();
    } catch (err) {
      console.error('cannot start a new chat:', err);
    }
  }, [aui, conversation, stopRunning]);

  // Opening a chat replaces the thread wholesale: the stored messages carry
  // their parent pointers and the active head, which is the shape the runtime
  // keeps internally.
  const openChat = useCallback(
    async (chat) => {
      // clicking a second chat while the first is still loading must not let
      // the slower response win
      const seq = ++opening.current;

      stopRunning();

      try {
        // fetched before anything is switched or selected, so a failure leaves
        // the chat you were in intact rather than half-swapped
        const stored = await listItems(chat.id);
        if (seq !== opening.current) return;

        // A chat belongs to one bot, so opening it adopts that bot: otherwise
        // the next message carries this chat's id with the other bot's model,
        // and the backend rejects the pair. previous.current is set in the same
        // tick so the selection-changed effect doesn't read this as a bot switch
        // and start a new chat on top of the one we are opening.
        const chatProject = chat.metadata?.project;
        const chatBot = chat.metadata?.bot;

        if (chatProject && chatBot) {
          previous.current = `${chatProject}/${chatBot}`;
          setProject(chatProject);
          setBot(chatBot);
        }

        await aui.threads.switchToNewThread();
        if (seq !== opening.current) return;

        aui.thread.import(toRepository(stored));

        conversation.current = chat.id;
        setCurrent(chat.id);
        setOpened((n) => n + 1);
      } catch (err) {
        console.error('cannot open chat:', err);
      }
    },
    [aui, conversation, stopRunning, setProject, setBot],
  );

  const renameChat = useCallback(async (chat, title) => {
    const trimmed = title.trim();
    if (trimmed === '' || trimmed === chat.metadata?.title) return;

    // optimistic: the list is the only place this title is shown, and a
    // round trip before the row updates reads as a broken edit
    setChats((current) =>
      current.map((c) =>
        c.id === chat.id ? { ...c, metadata: { ...c.metadata, title: trimmed } } : c,
      ),
    );

    try {
      await renameConversationAPI(chat.id, trimmed);
    } catch (err) {
      console.error('cannot rename chat:', err);
      reload();
    }
  }, [reload]);

  const removeChat = useCallback(
    async (chat) => {
      try {
        await deleteConversationAPI(chat.id);
      } catch (err) {
        console.error('cannot delete chat:', err);
        return;
      }

      if (conversation.current === chat.id) {
        newChat();
      }

      reload();
    },
    [conversation, newChat, reload],
  );

  // Optimistic like the rename, and for the same reason: the row has to move to
  // the pinned group on click, and a round trip before it does reads as a dead
  // button. reload() puts the server's order back.
  const pinChat = useCallback(
    async (chat, pinned) => {
      setChats((current) =>
        current.map((c) =>
          c.id === chat.id ? { ...c, metadata: { ...c.metadata, pinned: String(pinned) } } : c,
        ),
      );

      try {
        await pinConversationAPI(chat.id, pinned);
      } catch (err) {
        console.error('cannot pin chat:', err);
      }

      reload();
    },
    [reload],
  );

  // Answering a paused run's question. The decision is parked for the next run
  // to carry, then the run is started: there is no new message to send, which
  // is exactly what separates a resume from a turn.
  const answerQuestion = useCallback(
    (toolCallId, approved) => {
      if (!toolCallId) return;

      pending.current = { call_id: toolCallId, approved };

      const messages = aui.thread.getState().messages;
      const last = messages[messages.length - 1];

      aui.thread.startRun({ parentId: last?.id ?? null, sourceId: null, runConfig: {} });
    },
    [aui, pending],
  );

  // Branching is a copy: the chat up to this message becomes one of its own and
  // we switch to it, while the original is left exactly as it was.
  const forkChat = useCallback(
    async (messageID) => {
      const source = conversation.current;
      if (!source || !messageID) return;

      try {
        const forked = await forkConversationAPI(source, messageID);

        await openChat(forked);
        reload();
      } catch (err) {
        console.error('cannot branch chat:', err);
      }
    },
    [conversation, openChat, reload],
  );

  useEffect(() => {
    if (previous.current === selection) return;

    previous.current = selection;
    newChat();
  }, [selection, newChat]);

  const value = useMemo(
    () => ({
      chats,
      current,
      opened,
      isLoading,
      newChat,
      openChat,
      renameChat,
      removeChat,
      forkChat,
      pinChat,
      answerQuestion,
      reload,
    }),
    [
      chats,
      current,
      opened,
      isLoading,
      newChat,
      openChat,
      renameChat,
      removeChat,
      forkChat,
      pinChat,
      answerQuestion,
      reload,
    ],
  );

  return <ChatsContext.Provider value={value}>{children}</ChatsContext.Provider>;
}
